import { useNavigate, useLocation } from "react-router";
import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  UIEvent,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useAppAuth } from "@/hooks/useAppAuth";
import api from "../../http";
import { isToday, isYesterday, subDays } from "date-fns";
import { Bot, Edit, Menu, X, Archive, MoreHorizontal } from "lucide-react";
import { Thread } from "@/dto/thread";
import SettingsDropdown from "../assistant/SettingsDropdown";
import ProfileImageModal from "../assistant/ProfileImageModal";
import {
  appConfig,
  PLATFORM_BRANDING_LABEL,
  PLATFORM_BRANDING_LOGO,
  PLATFORM_BRANDING_URL,
} from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useI18n } from "@/contexts/I18nContext";

export interface MemberSidebarRef {
  clearSelection: () => void;
}

interface MemberSidebarProps {
  collapsed?: boolean;
}

const MemberSidebar = forwardRef<MemberSidebarRef, MemberSidebarProps>(
  ({}, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { getAccessToken } = useAppAuth();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState<{
      id: string;
      first_name?: string | null;
      last_name?: string | null;
      email: string;
      profile_image_url?: string | null;
    } | null>(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [threadToArchive, setThreadToArchive] = useState<string | null>(null);
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
    const { themeColor, logoName } = useColor();
    const { t } = useI18n();
    const APP_NAME = appConfig.features.isDemoMode ? logoName : appConfig.name;
    const APP_COLOR = appConfig.features.isDemoMode
      ? themeColor
      : appConfig.themeColor;

    // Demo mode: show text, production mode: show logo from config
    const LOGO_SRC = appConfig.logo;

    // Thread state (for assistant)
    const [threads, setThreads] = useState<Thread[]>([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedThreadId, setSelectedThreadId] = useState<string | null>(
      null,
    );
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // Close mobile menu when route changes
    useEffect(() => {
      setIsMobileOpen(false);
    }, [navigate]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = () => {
        if (openDropdown) {
          setOpenDropdown(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [openDropdown]);




    // Load current user information
    useEffect(() => {
      const loadCurrentUser = async () => {
        try {
          const token = await getAccessToken();
          const userData = await api.getCurrentUser(token);
          setCurrentUser(userData);
        } catch (error) {
          console.error("Failed to load current user:", error);
        }
      };

      loadCurrentUser();
    }, [getAccessToken]);

    const handleProfileImageUpdate = (imageUrl: string) => {
      if (currentUser) {
        setCurrentUser({
          ...currentUser,
          profile_image_url: imageUrl,
        });
      }
    };

    const handleUserInfoUpdate = (updatedUser: {
      id: string;
      first_name?: string | null;
      last_name?: string | null;
      email: string;
      profile_image_url?: string | null;
    }) => {
      setCurrentUser(updatedUser);
    };

    useImperativeHandle(ref, () => ({
      clearSelection: () => {
        setSelectedThreadId(null);
      },
    }));

    const fetchThreads = async (newOffset = 0) => {
      try {
        if (newOffset === 0) {
          setInitialLoading(true);
        } else {
          setLoadingMore(true);
        }

        const token = await getAccessToken();
        const response = await api.getThreads(token, newOffset, 20);

        if (newOffset === 0) {
          setThreads(response.threads);
        } else {
          // Merge and deduplicate threads by ID
          const existingIds = new Set(threads.map((t) => t.id));
          const uniqueNewThreads = response.threads.filter(
            (t) => !existingIds.has(t.id),
          );
          setThreads((prev) => [...prev, ...uniqueNewThreads]);
        }

        // Check if we've reached the end
        setHasMore(response.threads.length === 20);
        setOffset(newOffset);
      } catch (err) {
        console.error("Failed to fetch threads:", err);
        setError(t("assistant.errorGeneric"));
      } finally {
        setInitialLoading(false);
        setLoadingMore(false);
      }
    };

    useEffect(() => {
      fetchThreads();
    }, [getAccessToken]);

    // Sync selected thread from URL
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      const threadId = params.get("thread");
      setSelectedThreadId(threadId);
    }, [location.search]);

    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      // Load more when user scrolls to 80% of the way down
      if (scrollHeight - scrollTop <= clientHeight * 1.2) {
        if (!loadingMore && hasMore) {
          fetchThreads(offset + 20);
        }
      }
    };

    const handleThreadClick = (threadId: string) => {
      setSelectedThreadId(threadId);
      navigate(`/assistant?thread=${threadId}`);
    };

    const confirmArchive = async () => {
      if (!threadToArchive) return;
      
      try {
        const token = await getAccessToken();
        await api.archiveThread(threadToArchive, token);
        
        // Remove the thread from the local state
        setThreads(prevThreads => prevThreads.filter(thread => thread.id !== threadToArchive));
        
        // If the archived thread was selected, clear the selection
        if (selectedThreadId === threadToArchive) {
          setSelectedThreadId(null);
          navigate('/assistant?new=true&t=' + Date.now());
        }
      } catch (error) {
        console.error("Error archiving thread:", error);
      } finally {
        setShowArchiveConfirm(false);
        setThreadToArchive(null);
      }
    };

    const cancelArchive = () => {
      setShowArchiveConfirm(false);
      setThreadToArchive(null);
    };

    const handleDropdownToggle = (threadId: string, event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent thread selection when clicking dropdown
      
      if (openDropdown === threadId) {
        // Close dropdown
        setOpenDropdown(null);
        setDropdownPosition(null);
      } else {
        // Open dropdown
        setOpenDropdown(threadId);
        const button = buttonRefs.current[threadId];
        if (button) {
          const rect = button.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            right: window.innerWidth - rect.right,
          });
        }
      }
    };

    const handleNewChat = () => {
      setSelectedThreadId(null);
      navigate(`/assistant?new=true&t=${Date.now()}`);
    };

    const groupThreadsByDate = (threads: Thread[]) => {
      // Sort threads by updated_at date (newest first) to ensure chronological ordering
      const sortedThreads = [...threads].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );

      const today: Thread[] = [];
      const yesterday: Thread[] = [];
      const previous7: Thread[] = [];
      const previous30: Thread[] = [];
      const olderThan30: Thread[] = [];

      const now = new Date();
      const yesterday_date = subDays(now, 1);
      const sevenDaysAgo = subDays(now, 7);
      const thirtyDaysAgo = subDays(now, 30);

      sortedThreads.forEach((thread) => {
        const date = new Date(thread.created_at);

        if (isToday(date)) {
          today.push(thread);
        } else if (isYesterday(date)) {
          yesterday.push(thread);
        } else if (date >= sevenDaysAgo && date < yesterday_date) {
          previous7.push(thread);
        } else if (date >= thirtyDaysAgo && date < sevenDaysAgo) {
          previous30.push(thread);
        } else if (date < thirtyDaysAgo) {
          olderThan30.push(thread);
        }
      });

      return { today, yesterday, previous7, previous30, olderThan30 };
    };

    const renderThreadGroup = (label: string, threads: Thread[]) => {
      if (!threads || threads.length === 0) return null;

      return (
        <div className="mt-4 pb-4 border-b border-gray-200">
          <div className="text-md font-bold text-slate-800 uppercase px-4 mb-2">
            {label}
          </div>
          <ul className="space-y-2">
            {threads.map((thread) => (
              <li
                key={thread.id}
                className={`flex items-center justify-between group rounded px-4 py-1 ${
                  selectedThreadId === thread.id
                    ? "bg-slate-200"
                    : "hover:bg-slate-100"
                }`}
              >
                <div
                  onClick={() => handleThreadClick(thread.id)}
                  className={`flex-1 truncate text-sm cursor-pointer ${
                    selectedThreadId === thread.id
                      ? "font-semibold text-slate-800"
                      : "text-gray-600"
                  }`}
                >
                  {thread.title}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    ref={(el) => (buttonRefs.current[thread.id] = el)}
                    onClick={(e) => handleDropdownToggle(thread.id, e)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md border-none bg-transparent p-0 text-gray-500 transition-colors hover:bg-slate-100 hover:text-gray-700"
                    title={t("memberSidebar.threadActions")}
                  >
                    <MoreHorizontal size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    };

    const grouped = groupThreadsByDate(threads);
    const pageTitle = t("memberSidebar.pageTitle", { appName: APP_NAME });

    return (
      <>
        {/* Mobile Header - only show when sidebar is closed */}
        {!isMobileOpen && (
          <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
            {/* The parent container, acting as the reference for absolute positioning */}
            <div className="relative flex items-center justify-between h-full">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsMobileOpen(!isMobileOpen)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <Menu size={20} />
                </button>
              </div>

              {/* Centered Title (horizontally and vertically) */}
              <h2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-lg font-bold text-slate-800 text-center">
                {pageTitle}
              </h2>

              <div className="flex items-center gap-2">
                <SettingsDropdown
                  onOpenProfileModal={() => setIsProfileModalOpen(true)}
                  currentUser={currentUser}
                />
              </div>
            </div>
          </div>
        )}

        {/* Mobile Overlay */}
        {isMobileOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`
          fixed lg:static inset-y-0 left-0 z-40
          transform transition-transform duration-300 ease-in-out
          lg:transform-none
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          w-64 lg:w-auto
          bg-white border-r border-gray-200 shadow-lg lg:shadow-none
          rounded-3xl rounded-3xl lg:rounded-none
          lg:h-full
        `}  
        >
          <div className="flex flex-col h-full">
            {/* Logo + Icons - hidden on mobile */}
            <div className="hidden lg:flex p-4 items-center justify-between mb-4">
              <div className="flex flex-col items-start gap-0.5">
                <div className="flex items-center gap-2 min-w-0">
                  {appConfig.features.isDemoMode ? (
                    <span className="font-bold text-3xl" style={{ color: APP_COLOR }}>
                      {APP_NAME}
                    </span>
                  ) : (
                    <div className="min-w-0 flex-1 max-w-[200px]">
                      <img
                        src={LOGO_SRC}
                        alt="Logo"
                        className="block h-12 w-full object-contain object-left"
                      />
                    </div>
                  )}
                  <span className="shrink-0 bg-slate-100 text-xs px-2 py-1 rounded-md font-medium">
                    {t("role.beta")}
                  </span>
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-[10px] leading-none text-slate-400">
                    {PLATFORM_BRANDING_LABEL}
                  </span>
                  <a
                    href={PLATFORM_BRANDING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open Coorpix website"
                    className="block h-6 w-[92px] rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <img
                      src={PLATFORM_BRANDING_LOGO}
                      alt="Coorpix"
                      className="block h-full w-full object-contain object-left"
                    />
                  </a>
                </div>
              </div>
              <div className="flex justify-end gap-1 text-slate-500 text-xl">
                {/* Desktop edit button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleNewChat}
                      className="hidden lg:block p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none"
                    >
                      <Edit className="h-6 w-6 text-slate-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[320px]">
                    {t("memberSidebar.newChat")}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Mobile Header in Sidebar - only show when sidebar is open */}
            <div className="lg:hidden p-4 flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-start gap-0.5">
                  <div className="flex items-center gap-2 min-w-0">
                    {appConfig.features.isDemoMode ? (
                      <span className="font-bold text-xl" style={{ color: APP_COLOR }}>
                        {APP_NAME}
                      </span>
                    ) : (
                      <div className="min-w-0 flex-1 max-w-[150px]">
                        <img
                          src={LOGO_SRC}
                          alt="Logo"
                          className="block h-6 w-full object-contain object-left"
                        />
                      </div>
                    )}
                    <span className="shrink-0 bg-slate-100 text-xs px-2 py-1 rounded-md font-medium">
                      {t("role.beta")}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-[10px] leading-none text-slate-400">
                      {PLATFORM_BRANDING_LABEL}
                    </span>
                    <a
                      href={PLATFORM_BRANDING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open Coorpix website"
                      className="block h-5 w-[82px] rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <img
                        src={PLATFORM_BRANDING_LOGO}
                        alt="Coorpix"
                        className="block h-full w-full object-contain object-left"
                      />
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Mobile new chat button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleNewChat}
                      className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none"
                    >
                      <Edit className="h-6 w-6 text-slate-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[320px]">
                    {t("memberSidebar.newChat")}
                  </TooltipContent>
                </Tooltip>
                {/* Mobile close button */}
                <button
                  onClick={() => setIsMobileOpen(false)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Section Title */}
            <div className="px-4">
              <div className="flex items-center gap-2 w-full bg-[#f1f5f9] text-lg font-bold text-slate-800 p-2 rounded-md">
                <Bot className="h-6 w-6" style={{ color: appConfig.features.isDemoMode ? undefined : APP_COLOR }} />
                {t("memberSidebar.assistant")}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
              <div className="space-y-2">
                <div className="mt-6">
                  {error ? (
                    <div className="text-sm text-red-500 px-4">{error}</div>
                  ) : !initialLoading && threads.length === 0 ? (
                    <div className="text-sm text-gray-500 px-4">
                      {t("memberSidebar.noThreads")}
                    </div>
                  ) : (
                    !initialLoading && (
                      <>
                        {grouped.today.length > 0 &&
                          renderThreadGroup(t("memberSidebar.today"), grouped.today)}
                        {grouped.yesterday.length > 0 &&
                          renderThreadGroup(t("memberSidebar.yesterday"), grouped.yesterday)}
                        {grouped.previous7.length > 0 &&
                          renderThreadGroup(
                            t("memberSidebar.previous7"),
                            grouped.previous7,
                          )}
                        {grouped.previous30.length > 0 &&
                          renderThreadGroup(
                            t("memberSidebar.previous30"),
                            grouped.previous30,
                          )}
                        {grouped.olderThan30.length > 0 &&
                          renderThreadGroup(
                            t("memberSidebar.olderThan30"),
                            grouped.olderThan30,
                          )}
                      </>
                    )
                  )}
                </div>
              </div>
              {loadingMore && (
                <div className="text-center py-2 text-sm text-gray-500">
                  {t("memberSidebar.loadMore")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile content padding to prevent overlap */}
        <div className="lg:hidden h-16"></div>

        {/* Profile Image Modal */}
        {currentUser && (
          <ProfileImageModal
            isOpen={isProfileModalOpen}
            onClose={() => setIsProfileModalOpen(false)}
            onProfileImageUpdate={handleProfileImageUpdate}
            currentUser={currentUser}
            onUserInfoUpdate={handleUserInfoUpdate}
          />
        )}

        {/* Archive Confirmation Dialog */}
        <AlertDialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("memberSidebar.archiveTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("memberSidebar.archiveConfirm")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={cancelArchive}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-transparent hover:bg-slate-100 transition-colors text-gray-700 hover:text-gray-700"
              >
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmArchive}
                className="flex-1 px-4 py-2 text-white rounded-md transition-colors"
                style={{
                  backgroundColor: APP_COLOR,
                  borderColor: APP_COLOR
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = APP_COLOR;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.borderWidth = '1px';
                  e.currentTarget.style.filter = 'brightness(0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = APP_COLOR;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = APP_COLOR;
                  e.currentTarget.style.borderWidth = '1px';
                  e.currentTarget.style.filter = 'none';
                }}
              >
                {t("memberSidebar.archive")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Portal Dropdown Menu */}
        {openDropdown && dropdownPosition &&
          createPortal(
            <div
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                right: dropdownPosition.right,
                width: '128px',
              }}
              className="bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50"
            >
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenDropdown(null);
                  setDropdownPosition(null);
                  setThreadToArchive(openDropdown);
                  setShowArchiveConfirm(true);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <Archive size={14} />
                {t("memberSidebar.archive")}
              </div>
            </div>,
            document.body
          )}
      </>
    );
  },
);

export default MemberSidebar;
